/**
 * Abgabefristen der Umsatzsteuer-Voranmeldung (§ 18 Abs. 1, Abs. 2 UStG; §§ 46–48 UStDV; § 108 Abs. 3 AO).
 *
 * Basis: 10. Tag nach Ablauf des Voranmeldungszeitraums (Quartal: Folgemonat des Quartalsendes),
 * Dauerfristverlängerung + 1 Monat, dann Verschiebung auf den nächsten Werktag mit den Feiertagen
 * des Finanzamts-Bundeslandes. Fehlt das Bundesland, zählen nur Feiertage, die in ALLEN 16 Ländern
 * gelten (Schnittmenge) – die Union 'ALLE' würde Fristen fälschlich nach hinten schieben.
 *
 * Keine Systemuhr in der Engine: „heute" kommt ausschließlich über input.now.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {ALLE_BUNDESLAENDER, BundeslandCode, isRentBusinessDay, shiftToNextRentBusinessDay} from '../../../../helpers/werktage';
import {parsePeriodKey} from './month';

export interface DueDateInput {
    year: number;
    periodKey: string;
    dauerfrist: boolean;
    bundesland: BundeslandCode | null;
    now: Date;
}

export interface DueDateResult {
    dueDate: Date;              // gesetzliche Frist vor Werktagsverschiebung
    dueDateEffective: Date;     // nach § 108 Abs. 3 AO
    tageBisFrist: number;       // Kalendertage von now bis dueDateEffective (negativ = überfällig)
    istUeberfaellig: boolean;
    regionHint: string | null;  // gesetzt, wenn kein Bundesland hinterlegt ist
}

export const REGION_HINT = 'Bundesland des Finanzamts hinterlegen – bis dahin gelten nur Feiertage, die in allen 16 Ländern gelten.';

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Werktag in mindestens einem Bundesland → Schnittmenge der Feiertage. */
export const isBusinessDayInAnyState = (date: Date): boolean => ALLE_BUNDESLAENDER.some(code => isRentBusinessDay(date, code));

export const shiftToNextBusinessDayIntersection = (date: Date): Date => {
    const shifted = startOfDay(date);
    while (!isBusinessDayInAnyState(shifted)) {
        shifted.setDate(shifted.getDate() + 1);
    }
    return shifted;
};

/** Kalendertage zwischen zwei lokalen Tagen – über Date.UTC, damit Sommerzeitwechsel keinen Tag kosten. */
export const daysBetween = (from: Date, to: Date): number => {
    const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
    const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((b - a) / 86_400_000);
};

export const computeDueDate = (input: DueDateInput): DueDateResult => {
    const parsed = parsePeriodKey(input.periodKey);
    if (!parsed) {
        throw new Error(`Ungültiger periodKey: ${input.periodKey}`);
    }
    // letzter Monat des VAZ, 0-basiert: M03 → 2, Q1 → 2, Q4 → 11
    const endMonth0 = parsed.type === 'MONAT' ? parsed.index - 1 : parsed.index * 3 - 1;
    const monthsAfterEnd = 1 + (input.dauerfrist ? 1 : 0);
    const dueDate = new Date(input.year, endMonth0 + monthsAfterEnd, 10); // Date rollt über den Jahreswechsel
    const dueDateEffective = input.bundesland
        ? shiftToNextRentBusinessDay(dueDate, input.bundesland)
        : shiftToNextBusinessDayIntersection(dueDate);
    const tageBisFrist = daysBetween(input.now, dueDateEffective);
    return {
        dueDate,
        dueDateEffective,
        tageBisFrist,
        istUeberfaellig: tageBisFrist < 0,
        regionHint: input.bundesland ? null : REGION_HINT,
    };
};
